import type { DeepPartial, FlowUITheme } from "@akin2unde/flowui-react";
import type { TenantThemeSettings } from "../models/TenantThemeSettings";

export const DefaultTenantTheme: TenantThemeSettings = {
  PrimaryColor: "#9333ea",
  SecondaryColor: "#0f766e",
  CanvasColor: "#f8fafc",
  SurfaceColor: "#ffffff",
  TextColor: "#0f172a",
  BorderColor: "#e2e8f0",
  CornerRadius: 10,
};

export function CreatePantryStackTheme(
  settings: TenantThemeSettings = DefaultTenantTheme,
): DeepPartial<FlowUITheme> {
  const primaryColor = NormalizeHexColor(
    settings.PrimaryColor,
    DefaultTenantTheme.PrimaryColor,
  );
  const secondaryColor = NormalizeHexColor(
    settings.SecondaryColor,
    DefaultTenantTheme.SecondaryColor,
  );
  const primary = CreateColorScale(primaryColor);
  const secondary = CreateColorScale(secondaryColor);

  return {
    palette: {
      primary,
      secondary,
    },
    schemes: {
      light: {
        canvas: NormalizeHexColor(
          settings.CanvasColor,
          DefaultTenantTheme.CanvasColor,
        ),
        surface: NormalizeHexColor(
          settings.SurfaceColor,
          DefaultTenantTheme.SurfaceColor,
        ),
        surfaceRaised: NormalizeHexColor(
          settings.SurfaceColor,
          DefaultTenantTheme.SurfaceColor,
        ),
        surfaceSunken: MixColors(
          NormalizeHexColor(
            settings.CanvasColor,
            DefaultTenantTheme.CanvasColor,
          ),
          "#000000",
          0.03,
        ),
        text: NormalizeHexColor(
          settings.TextColor,
          DefaultTenantTheme.TextColor,
        ),
        textMuted: MixColors(
          NormalizeHexColor(settings.TextColor, DefaultTenantTheme.TextColor),
          NormalizeHexColor(
            settings.CanvasColor,
            DefaultTenantTheme.CanvasColor,
          ),
          0.42,
        ),
        border: NormalizeHexColor(
          settings.BorderColor,
          DefaultTenantTheme.BorderColor,
        ),
        borderStrong: MixColors(
          NormalizeHexColor(
            settings.BorderColor,
            DefaultTenantTheme.BorderColor,
          ),
          NormalizeHexColor(settings.TextColor, DefaultTenantTheme.TextColor),
          0.2,
        ),
        focus: primaryColor,
      },
    },
    radius: {
      sm: `${Math.max(settings.CornerRadius - 4, 0)}px`,
      md: `${Math.max(settings.CornerRadius - 2, 0)}px`,
      lg: `${settings.CornerRadius}px`,
      xl: `${settings.CornerRadius + 6}px`,
    },
  };
}

function NormalizeHexColor(color: string, fallback: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) || /^#[0-9a-f]{3}$/i.test(color)
    ? color
    : fallback;
}

export const PantryStackTheme = CreatePantryStackTheme();

function CreateColorScale(color: string): Record<number, string> {
  return {
    50: MixColors(color, "#ffffff", 0.94),
    100: MixColors(color, "#ffffff", 0.86),
    200: MixColors(color, "#ffffff", 0.72),
    300: MixColors(color, "#ffffff", 0.52),
    400: MixColors(color, "#ffffff", 0.28),
    500: MixColors(color, "#ffffff", 0.08),
    600: color,
    700: MixColors(color, "#000000", 0.16),
    800: MixColors(color, "#000000", 0.3),
    900: MixColors(color, "#000000", 0.44),
    950: MixColors(color, "#000000", 0.62),
  };
}

function MixColors(first: string, second: string, weight: number): string {
  const firstRgb = HexToRgb(first);
  const secondRgb = HexToRgb(second);
  const channel = (start: number, end: number): number =>
    Math.round(start + (end - start) * weight);

  return `#${[
    channel(firstRgb[0], secondRgb[0]),
    channel(firstRgb[1], secondRgb[1]),
    channel(firstRgb[2], secondRgb[2]),
  ]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}

function HexToRgb(color: string): [number, number, number] {
  const normalized = color.replace("#", "");
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((value) => value + value)
          .join("")
      : normalized.padEnd(6, "0").slice(0, 6);

  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}
